import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import SEOMeta from './SEOMeta';

const EFFECTIVE_DATE  = 'May 6, 2026';
const COMPANY_NAME    = 'NivasaPro Inc.';
const CONTACT_EMAIL   = 'privacy@nivasapro.ai';
const SITE_URL        = 'https://www.nivasapro.ai';
const DPO_EMAIL       = 'dpo@nivasapro.ai';

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
      {title}
    </h2>
    <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
      {children}
    </div>
  </section>
);

const SubSection = ({ title, children }) => (
  <div>
    <h3 className="font-semibold text-gray-800 mb-1">{title}</h3>
    {children}
  </div>
);

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEOMeta
        title="Privacy Policy"
        description="NivasaPro Privacy Policy: how we collect, use, and protect your personal information."
        canonical="/privacy"
        noIndex
      />

      {/* Nav */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <RouterLink to="/" className="flex items-center space-x-2">
            <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-7 w-7" />
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              NivasaPro.ai
            </span>
          </RouterLink>
          <RouterLink to="/" className="text-sm text-cyan-600 hover:underline font-medium">
            ← Back to Home
          </RouterLink>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Privacy Policy</h1>
          <p className="text-sm text-gray-500">
            Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last updated: {EFFECTIVE_DATE}
          </p>
          <div className="mt-4 p-4 bg-cyan-50 border border-cyan-200 rounded-lg text-sm text-cyan-800">
            This Privacy Policy explains how {COMPANY_NAME} collects, uses, shares, and protects
            information about you when you use the NivasaPro platform and Niva AI assistant. Please
            read it carefully.
          </div>
        </div>

        {/* Table of Contents */}
        <div className="mb-10 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
          <h2 className="text-base font-semibold text-gray-800 mb-3">Contents</h2>
          <ol className="space-y-1 text-sm text-cyan-700 list-decimal list-inside columns-2">
            {[
              ['who-we-are',        'Who We Are'],
              ['information',       'Information We Collect'],
              ['how-we-use',        'How We Use Your Information'],
              ['legal-basis',       'Legal Basis for Processing'],
              ['sharing',           'Sharing and Disclosure'],
              ['ai-processing',     'AI and Automated Processing'],
              ['cookies',           'Cookies and Tracking'],
              ['retention',         'Data Retention'],
              ['security',          'Security'],
              ['your-rights',       'Your Privacy Rights'],
              ['children',          'Children\'s Privacy'],
              ['international',     'International Data Transfers'],
              ['third-party',       'Third-Party Links and Services'],
              ['changes',           'Changes to This Policy'],
              ['contact',           'Contact Us'],
            ].map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="hover:underline">{label}</a>
              </li>
            ))}
          </ol>
        </div>

        {/* ── 1. Who We Are ── */}
        <Section id="who-we-are" title="1. Who We Are">
          <p>
            {COMPANY_NAME} ("NivasaPro", "we", "us", or "our") operates the NivasaPro property
            management platform, including the Niva AI assistant, available at{' '}
            <a href={SITE_URL} className="text-cyan-600 hover:underline">{SITE_URL}</a> and
            associated mobile and API interfaces (the "Service").
          </p>
          <p>
            NivasaPro acts as the data controller for personal information collected through the
            Service. For questions about this policy or your data, contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-600 hover:underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        {/* ── 2. Information We Collect ── */}
        <Section id="information" title="2. Information We Collect">
          <SubSection title="2.1 Information You Provide Directly">
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Account information</strong>: name, email address, password, and user type (landlord or tenant) when you register.</li>
              <li><strong>Property and portfolio data</strong>: property addresses, unit details, and related management information you enter.</li>
              <li><strong>Tenant information</strong>: names, contact details, and lease information for tenants you add to your portfolio.</li>
              <li><strong>Maintenance records</strong>: descriptions of issues, photographs, communications with service providers, and repair histories.</li>
              <li><strong>Financial data</strong>: rent amounts, payment records, cost entries, and expense details you record in the platform.</li>
              <li><strong>Communications</strong>: messages you send to Niva, support tickets, and any other correspondence with us.</li>
              <li><strong>ROMI Assessment responses</strong>: if you take the free NivasaPro ROMI Score™ assessment, we collect your conversational responses, name, and email address.</li>
            </ul>
          </SubSection>

          <SubSection title="2.2 Information Collected Automatically">
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Usage data</strong>: pages visited, features used, session duration, click patterns, and navigation paths.</li>
              <li><strong>Device and technical data</strong>: IP address, browser type and version, operating system, device identifiers, and time zone.</li>
              <li><strong>Log data</strong>: server logs recording requests to our API, including timestamps and response codes.</li>
              <li><strong>Cookies and similar technologies</strong>: see Section 7 for details.</li>
            </ul>
          </SubSection>

          <SubSection title="2.3 Information from Third Parties">
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Communication providers</strong>: if you interact with Niva via SMS or WhatsApp, we receive your phone number and message content from Twilio.</li>
              <li><strong>Payment processors</strong>: if you subscribe to a paid plan, our payment processor (Stripe) handles payment card data. We receive only limited transaction metadata; we do not store full card numbers.</li>
            </ul>
          </SubSection>
        </Section>

        {/* ── 3. How We Use Your Information ── */}
        <Section id="how-we-use" title="3. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc list-inside space-y-1.5 ml-2">
            <li>Provide, operate, and improve the NivasaPro platform and Niva AI assistant.</li>
            <li>Process and respond to maintenance requests, approvals, and tenant communications on your behalf.</li>
            <li>Send transactional emails such as maintenance approval requests, repair notifications, rent reminders, and account alerts via Mailgun.</li>
            <li>Generate AI-powered insights, summaries, triage assessments, and the ROMI Score™ report.</li>
            <li>Authenticate users and maintain account security using JSON Web Tokens (JWT).</li>
            <li>Analyse usage patterns to improve product features and fix bugs.</li>
            <li>Comply with legal obligations and enforce our Terms of Service.</li>
            <li>Communicate with you about product updates, new features, and relevant content (you may opt out at any time).</li>
            <li>Respond to ROMI Score™ assessment leads and send the personalised report to the email address you provide.</li>
          </ul>
          <p>
            We do not sell your personal information to third parties. We do not use your property
            or tenant data to train AI models without explicit consent.
          </p>
        </Section>

        {/* ── 4. Legal Basis ── */}
        <Section id="legal-basis" title="4. Legal Basis for Processing (GDPR)">
          <p>
            If you are located in the European Economic Area (EEA) or United Kingdom, we process
            your personal data under the following legal bases:
          </p>
          <ul className="list-disc list-inside space-y-1.5 ml-2">
            <li><strong>Contract performance</strong>: processing necessary to provide the Service under our Terms of Service (e.g., managing maintenance requests, sending notifications).</li>
            <li><strong>Legitimate interests</strong>: improving our platform, preventing fraud, and ensuring security, where those interests are not overridden by your rights.</li>
            <li><strong>Legal obligation</strong>: processing required to comply with applicable law.</li>
            <li><strong>Consent</strong>: for marketing communications and optional analytics. You may withdraw consent at any time without affecting prior processing.</li>
          </ul>
        </Section>

        {/* ── 5. Sharing ── */}
        <Section id="sharing" title="5. Sharing and Disclosure">
          <p>We share your information only in the following circumstances:</p>

          <SubSection title="5.1 Service Providers">
            <p>
              We work with trusted third-party vendors who process data on our behalf under
              strict data processing agreements:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
              <li><strong>MongoDB Atlas</strong>: database hosting and storage.</li>
              <li><strong>Google Cloud / Firebase</strong>: application hosting and frontend delivery.</li>
              <li><strong>Mailgun</strong>: transactional email delivery.</li>
              <li><strong>Twilio</strong>: SMS and WhatsApp message routing.</li>
              <li><strong>Stripe</strong>: payment processing.</li>
              <li><strong>Anthropic / Google DeepMind</strong>: AI language model inference for Niva and the ROMI Assessment. Conversation content is sent to these providers solely to generate responses.</li>
              <li><strong>Cloudflare</strong>: bot protection and CDN.</li>
            </ul>
          </SubSection>

          <SubSection title="5.2 Legal Requirements">
            <p>
              We may disclose information where required by law, court order, or governmental
              authority, or where necessary to protect the rights, property, or safety of
              NivasaPro, our users, or the public.
            </p>
          </SubSection>

          <SubSection title="5.3 Business Transfers">
            <p>
              If NivasaPro is involved in a merger, acquisition, or sale of assets, your
              information may be transferred as part of that transaction. We will notify you
              before your personal information is transferred and becomes subject to a different
              privacy policy.
            </p>
          </SubSection>

          <SubSection title="5.4 With Your Consent">
            <p>
              We may share information in other circumstances with your explicit consent.
            </p>
          </SubSection>
        </Section>

        {/* ── 6. AI Processing ── */}
        <Section id="ai-processing" title="6. AI and Automated Processing">
          <p>
            NivasaPro uses AI language models (currently Claude by Anthropic and Gemini by Google)
            to power the Niva assistant and the ROMI Score™ assessment. When you interact with
            Niva, your messages and relevant context (property details, maintenance history) are
            sent to the AI provider to generate a response.
          </p>
          <p>
            <strong>What this means for your data:</strong>
          </p>
          <ul className="list-disc list-inside space-y-1.5 ml-2">
            <li>Conversation content is transmitted to AI providers for the sole purpose of generating responses.</li>
            <li>We configure these providers under zero-data-retention policies where available, meaning conversation content is not used to train their models.</li>
            <li>AI-generated outputs (maintenance summaries, triage assessments, ROMI scores) are stored in your account and may be seen by other authorised users of your organisation.</li>
            <li>Automated decisions made by Niva (such as maintenance priority classification) can be reviewed, overridden, or appealed by contacting support.</li>
          </ul>
          <p>
            No automated decision made by Niva produces legal or similarly significant effects
            without human review.
          </p>
        </Section>

        {/* ── 7. Cookies ── */}
        <Section id="cookies" title="7. Cookies and Tracking Technologies">
          <p>We use the following types of cookies and similar technologies:</p>
          <ul className="list-disc list-inside space-y-1.5 ml-2">
            <li><strong>Strictly necessary cookies</strong>: required for authentication (JWT session management) and security. These cannot be disabled.</li>
            <li><strong>Functional cookies</strong>: remember your preferences such as theme and language settings.</li>
            <li><strong>Analytics cookies</strong>: help us understand how the Service is used (e.g., page views, feature engagement). We use privacy-respecting analytics and do not share this data with advertising networks.</li>
            <li><strong>Bot protection</strong>: Cloudflare Turnstile is used on public forms to distinguish humans from automated bots. Turnstile may set cookies or use browser signals for this purpose.</li>
          </ul>
          <p>
            You can control non-essential cookies through your browser settings. Disabling
            strictly necessary cookies will impair your ability to use the Service.
          </p>
        </Section>

        {/* ── 8. Retention ── */}
        <Section id="retention" title="8. Data Retention">
          <p>
            We retain your personal information for as long as your account is active or as
            needed to provide the Service. Specifically:
          </p>
          <ul className="list-disc list-inside space-y-1.5 ml-2">
            <li><strong>Account data</strong>: retained for the duration of your account plus 30 days after deletion to allow recovery.</li>
            <li><strong>Maintenance and financial records</strong>: retained for 7 years to support legal and tax obligations, unless you request earlier deletion.</li>
            <li><strong>AI conversation transcripts</strong>: retained for 12 months, then anonymised or deleted.</li>
            <li><strong>ROMI Assessment data</strong>: retained for 24 months from the date of assessment. You may request deletion at any time.</li>
            <li><strong>Server logs</strong>: retained for 90 days.</li>
            <li><strong>Marketing preferences</strong>: retained until you unsubscribe or request deletion.</li>
          </ul>
          <p>
            When retention periods expire, data is securely deleted or anonymised so it can no
            longer be associated with you.
          </p>
        </Section>

        {/* ── 9. Security ── */}
        <Section id="security" title="9. Security">
          <p>
            We implement industry-standard technical and organisational measures to protect your
            personal information, including:
          </p>
          <ul className="list-disc list-inside space-y-1.5 ml-2">
            <li>Encryption in transit (TLS 1.2+) for all data transmitted between your browser and our servers.</li>
            <li>Encrypted storage for sensitive fields including passwords (bcrypt hashing) and API keys.</li>
            <li>JWT-based authentication with short-lived tokens and secure signing secrets.</li>
            <li>Role-based access controls limiting data access to authorised personnel.</li>
            <li>Regular dependency updates and security patching.</li>
            <li>MongoDB Atlas security features including network isolation and encryption at rest.</li>
          </ul>
          <p>
            No method of transmission or storage is 100% secure. If you suspect a security
            incident, please contact us immediately at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-600 hover:underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        {/* ── 10. Your Rights ── */}
        <Section id="your-rights" title="10. Your Privacy Rights">
          <SubSection title="10.1 Rights under GDPR (EEA / UK users)">
            <p>If you are located in the EEA or UK, you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
              <li><strong>Access</strong>: request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification</strong>: ask us to correct inaccurate or incomplete data.</li>
              <li><strong>Erasure</strong>: request deletion of your personal data ("right to be forgotten"), subject to legal retention requirements.</li>
              <li><strong>Restriction</strong>: ask us to limit processing of your data in certain circumstances.</li>
              <li><strong>Portability</strong>: receive your data in a structured, machine-readable format.</li>
              <li><strong>Objection</strong>: object to processing based on legitimate interests or for direct marketing.</li>
              <li><strong>Withdraw consent</strong>: where processing is based on consent, withdraw it at any time.</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, email{' '}
              <a href={`mailto:${DPO_EMAIL}`} className="text-cyan-600 hover:underline">{DPO_EMAIL}</a>.
              We will respond within 30 days. You also have the right to lodge a complaint with
              your local supervisory authority.
            </p>
          </SubSection>

          <SubSection title="10.2 Rights under CCPA (California residents)">
            <p>If you are a California resident, you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
              <li>Know what personal information we collect, use, disclose, and sell.</li>
              <li>Delete personal information we have collected, subject to exceptions.</li>
              <li>Opt out of the sale of personal information. <strong>We do not sell personal information</strong>.</li>
              <li>Non-discrimination for exercising your privacy rights.</li>
            </ul>
            <p className="mt-2">
              To exercise CCPA rights, email{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-600 hover:underline">{CONTACT_EMAIL}</a>{' '}
              with the subject line "CCPA Request".
            </p>
          </SubSection>

          <SubSection title="10.3 Email and Marketing Opt-Out">
            <p>
              You may unsubscribe from marketing emails at any time by clicking the unsubscribe
              link in any email we send or by contacting{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-600 hover:underline">{CONTACT_EMAIL}</a>.
              Transactional emails (maintenance alerts, approval requests, rent reminders) are
              necessary for the Service and cannot be disabled while your account is active.
            </p>
          </SubSection>
        </Section>

        {/* ── 11. Children ── */}
        <Section id="children" title="11. Children's Privacy">
          <p>
            The Service is not directed at children under 18 years of age. We do not knowingly
            collect personal information from anyone under 18. If you believe we have
            inadvertently collected such information, please contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-600 hover:underline">{CONTACT_EMAIL}</a>{' '}
            and we will delete it promptly.
          </p>
        </Section>

        {/* ── 12. International Transfers ── */}
        <Section id="international" title="12. International Data Transfers">
          <p>
            NivasaPro is operated primarily from the United States. If you access the Service
            from outside the United States, your information may be transferred to and processed
            in the United States or other countries where our service providers operate.
          </p>
          <p>
            For transfers from the EEA or UK to the United States, we rely on Standard
            Contractual Clauses (SCCs) approved by the European Commission, and/or the
            UK International Data Transfer Addendum, to ensure an adequate level of protection.
          </p>
        </Section>

        {/* ── 13. Third-Party Links ── */}
        <Section id="third-party" title="13. Third-Party Links and Services">
          <p>
            The Service may contain links to third-party websites or integrations. This Privacy
            Policy does not apply to those third-party services. We encourage you to review the
            privacy policies of any third-party services you access through NivasaPro. We are not
            responsible for the privacy practices of third parties.
          </p>
        </Section>

        {/* ── 14. Changes ── */}
        <Section id="changes" title="14. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our
            practices, technology, legal requirements, or other factors. When we make material
            changes, we will:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Update the "Effective date" at the top of this page.</li>
            <li>Notify registered users by email or in-app notification at least 14 days before the changes take effect.</li>
            <li>For significant changes affecting how we process data you have already provided, seek your consent where required by law.</li>
          </ul>
          <p>
            Continued use of the Service after the effective date constitutes acceptance of the
            revised policy. If you do not agree, you should delete your account before the
            changes take effect.
          </p>
        </Section>

        {/* ── 15. Contact ── */}
        <Section id="contact" title="15. Contact Us">
          <p>
            For privacy-related questions, requests, or concerns, please contact us at:
          </p>
          <div className="mt-3 p-4 bg-white rounded-lg border border-gray-200 text-sm space-y-1">
            <p><strong>{COMPANY_NAME}</strong></p>
            <p>Privacy Enquiries: <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-600 hover:underline">{CONTACT_EMAIL}</a></p>
            <p>Data Protection Officer: <a href={`mailto:${DPO_EMAIL}`} className="text-cyan-600 hover:underline">{DPO_EMAIL}</a></p>
            <p>Website: <a href={SITE_URL} className="text-cyan-600 hover:underline">{SITE_URL}</a></p>
          </div>
          <p className="mt-3">
            We aim to respond to all privacy requests within <strong>30 days</strong>. For
            urgent security concerns, please mark your subject line "URGENT: Security".
          </p>
        </Section>

        {/* Footer links */}
        <div className="mt-12 pt-6 border-t border-gray-200 flex gap-4 text-sm text-gray-500">
          <RouterLink to="/terms" className="hover:text-cyan-600 hover:underline">Terms of Service</RouterLink>
          <RouterLink to="/" className="hover:text-cyan-600 hover:underline">Back to Home</RouterLink>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
